"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { Navigation } from "@/components/Navigation";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Crown, RefrigeratorIcon, Microwave, ChefHat, Lock, Pencil } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import StructuredData from "@/components/StructuredData";
import { generatePageTitle, generateMetaDescription, generateKeywordsString } from "@/lib/seoUtils";

const Profile = () => {
  const { user, loading, signOut, updateProfile } = useAuth();
  const router = useRouter();
  const { plan, usage, loading: subLoading } = useSubscription();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const { toast } = useToast();
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      router.push("/auth");
    }
  }, [user, loading]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeSection === 'ingredients') {
      router.push('/profile/ingredients');
    }
  }, [activeSection]);

  const handleUpdateName = async () => {
    if (!newDisplayName.trim()) {
      toast({
        title: "שגיאה",
        description: "יש להזין שם",
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);

    try {
      const { error } = await updateProfile(newDisplayName.trim());

      if (error) {
        toast({
          title: "שגיאה בעדכון שם",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "השם עודכן בהצלחה!",
          description: "השם המלא שלך עודכן במערכת",
        });
        setIsEditNameOpen(false);
        setNewDisplayName("");
      }
    } catch (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בעדכון השם. נסה שוב מאוחר יותר.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const navigationCards = [
    {
      id: 'ingredients',
      title: 'המצרכים שלי',
      description: 'נהל את המצרכים שלך לפי קטגוריות',
      icon: RefrigeratorIcon,
      color: 'bg-orange-50 border-orange-200 text-orange-700',
      iconColor: 'text-orange-600',
      available: true
    },
    {
      id: 'appliances',
      title: 'המכשירים שלי',
      description: 'נהל את המכשירים והכלים שלך',
      icon: Microwave,
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      iconColor: 'text-blue-600',
      available: false
    },
    {
      id: 'chefs',
      title: 'השפים שלי',
      description: 'נהל את השפים והעדפות הבישול שלך',
      icon: ChefHat,
      color: 'bg-green-50 border-green-200 text-green-700',
      iconColor: 'text-green-600',
      available: false
    }
  ];

  const pageTitle = "הפרופיל שלי - Feedly";
  const metaDescription = generateMetaDescription(
    "הפרופיל שלי",
    "נהל את החשבון שלך, צפה בתוכנית המנוי, גש למצרכים שלך ולכל התכונות האישיות של Feedly"
  );

  return (
    <>
      {/* Metadata moved to metadata export or layout */}

      {/* Website structured data */}
      <StructuredData type="website" />

      <div className="min-h-screen bg-background" dir="rtl">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* User Dashboard Header - Only show on main profile page */}
            {!activeSection && (
              <Card className="mb-8 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    {/* Welcome & Plan */}
                    <div className="flex items-center gap-4">
                      <div className="bg-orange-100 p-3 rounded-full">
                        <User className="h-8 w-8 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-2xl font-bold text-gray-800">
                            שלום, {user.user_metadata?.display_name || user.email}!
                          </h2>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-orange-100"
                            onClick={() => {
                              setNewDisplayName(user.user_metadata?.display_name || "");
                              setIsEditNameOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4 text-orange-600" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Crown className="h-4 w-4 text-amber-500" />
                          <span>
                            התוכנית שלך:{' '}
                            <span className="font-semibold text-gray-700">
                              {subLoading ? 'טוען...' : plan?.name_he}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation Cards */}
            {!activeSection && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {navigationCards.map((card) => {
                  const IconComponent = card.icon;
                  return (
                    <Card
                      key={card.id}
                      className={`${card.color} border-2 hover:shadow-md transition-all cursor-pointer ${!card.available ? 'opacity-60' : 'hover:scale-105'
                        }`}
                      onClick={() => card.available && setActiveSection(card.id)}
                    >
                      <CardContent className="p-6 text-center">
                        <div className="flex flex-col items-center gap-4">
                          <div className={`p-4 rounded-full bg-white/50 ${card.iconColor}`}>
                            <IconComponent className="h-8 w-8" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                            <p className="text-sm opacity-80 mb-4">{card.description}</p>
                            {!card.available && (
                              <div className="flex items-center justify-center gap-2 text-sm opacity-60">
                                <Lock className="h-4 w-4" />
                                <span>בפיתוח</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}


            {/* Active Section Content */}

            {activeSection === 'appliances' && (
              <Card className="p-8 text-center">
                <Microwave className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">המכשירים שלי</h3>
                <p className="text-muted-foreground">תכונה זו תגיע בקרוב!</p>
              </Card>
            )}

            {activeSection === 'chefs' && (
              <Card className="p-8 text-center">
                <ChefHat className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">השפים שלי</h3>
                <p className="text-muted-foreground">תכונה זו תגיע בקרוב!</p>
              </Card>
            )}


          </div>
        </div>

        {/* Edit Name Dialog */}
        <Dialog open={isEditNameOpen} onOpenChange={setIsEditNameOpen}>
          <DialogContent className="sm:max-w-md" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-right">עריכת שם</DialogTitle>
              <DialogDescription className="text-right">
                עדכן את השם המלא שלך במערכת
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="display-name">שם מלא</Label>
                <Input
                  id="display-name"
                  placeholder="הזן את השם המלא שלך"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  className="h-11"
                  disabled={isUpdating}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isUpdating) {
                      handleUpdateName();
                    }
                  }}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditNameOpen(false)}
                  disabled={isUpdating}
                >
                  ביטול
                </Button>
                <Button
                  onClick={handleUpdateName}
                  disabled={isUpdating}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {isUpdating ? "מעדכן..." : "שמור"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Profile;